import { Button, InputNumber, Popconfirm, Table, Typography } from "antd";
import React, { useEffect } from "react";
import { FaCogs, FaTrashAlt } from "react-icons/fa";
import _ from "lodash";
import numeral from "numeral";

const { Text } = Typography;

const ManageProductTable = ({
  dataSource,
  setDataSource,
  onDeleteProduct,
  isEdit,
}) => {
  const columns = [
    {
      title: "#",
      key: "row_index",
      align: "center",
      width: "5%",
      render: (_, __, index) => index + 1,
    },
    {
      title: "รหัสสินค้า",
      dataIndex: "product_code",
      key: "product_code",
      align: "center",
      width: "15%",
    },
    {
      title: "ชื่อสินค้า",
      dataIndex: "product_name",
      key: "product_name",
      align: "center",
      width: "30%",
    },
    {
      title: "จำนวนบรรจุต่อ 1 ลัง",
      dataIndex: "unit_per_package",
      key: "unit_per_package",
      align: "center",
      width: "10%",
      render: (text) =>
        text === 0 ? (
          <Text type="danger">ไม่มีข้อมูล</Text>
        ) : (
          numeral(text).format("0,0.[00]")
        ),
    },
    {
      title: "ปริมาตรต่อ 1 ลัง",
      dataIndex: "volume_per_package",
      key: "volume_per_package",
      align: "center",
      width: "10%",
      render: (text) =>
        text === 0 ? (
          <Text type="danger">ไม่มีข้อมูล</Text>
        ) : (
          numeral(text).format("0,0.[00]")
        ),
    },
    {
      title: "จำนวนลัง",
      dataIndex: "package_qty",
      key: "package_qty",
      align: "center",
      width: "10%",
      render: (text, record) => {
        const unitPerPackage = Number(record.unit_per_package) || 0;
        const stockBalance = Number(record.stock_balance) || 0;

        const maxPackageQty =
          unitPerPackage > 0
            ? Math.floor(stockBalance / unitPerPackage)
            : undefined;

        return (
          <InputNumber
            size="small"
            disabled={!isEdit}
            min={0}
            max={maxPackageQty}
            precision={0}
            value={Number(text) || 0}
            onFocus={(e) => e.target.select()}
            onChange={(value) => {
              const package_qty = Math.max(Number(value) || 0, 0);

              setDataSource((prev) => ({
                ...prev,
                product_list: prev.product_list.map((item) => {
                  if (item.product_code !== record.product_code) return item;
                  // ไม่มีจำนวนชิ้นต่อลัง ให้แก้จำนวนลังแยกจากจำนวนชิ้น
                  if (unitPerPackage <= 0) {
                    return {
                      ...item,
                      package_qty,
                      total_volume:
                        package_qty * (Number(item.volume_per_package) || 0),
                    };
                  }
                  // มีจำนวนชิ้นต่อลัง ใช้ logic เดิม
                  const total_qty = Math.min(
                    package_qty * unitPerPackage,
                    stockBalance,
                  );

                  return {
                    ...item,
                    package_qty,
                    total_qty,
                    total_volume:
                      package_qty * (Number(item.volume_per_package) || 0),
                  };
                }),
              }));
            }}
          />
        );
      },
    },
    {
      title: "จำนวนชิ้นที่ขอเบิก",
      dataIndex: "total_qty",
      key: "total_qty",
      align: "center",
      width: "10%",
      render: (text, record) => {
        const unitPerPackage = Number(record.unit_per_package) || 0;
        const stockBalance = Number(record.stock_balance) || 0;

        return (
          <div
            style={{
              position: "relative",
            }}
          >
            <InputNumber
              size="small"
              disabled={!isEdit}
              precision={0}
              min={0}
              max={stockBalance}
              value={Number(text) || 0}
              onFocus={(e) => e.target.select()}
              onChange={(value) => {
                const total_qty = Math.min(
                  Math.max(Number(value) || 0, 0),
                  stockBalance,
                );

                setDataSource((prev) => ({
                  ...prev,
                  product_list: prev.product_list.map((item) => {
                    if (item.product_code !== record.product_code) return item;

                    // ไม่มีจำนวนชิ้นต่อลัง ไม่ต้องคำนวณจำนวนลังใหม่
                    if (unitPerPackage <= 0) {
                      return {
                        ...item,
                        total_qty,
                      };
                    }

                    // มีจำนวนชิ้นต่อลัง ใช้ logic เดิม
                    const package_qty = Math.floor(total_qty / unitPerPackage);

                    return {
                      ...item,
                      total_qty,
                      package_qty,
                      total_volume:
                        package_qty * (Number(item.volume_per_package) || 0),
                    };
                  }),
                }));
              }}
            />
            {unitPerPackage > 0 &&
              (Number(record.total_qty) || 0) % unitPerPackage > 0 && (
                <Text
                  type="danger"
                  style={{
                    position: "absolute",
                    top: 24,
                    right: 0,
                    fontSize: 12,
                  }}
                >
                  เศษ {(Number(record.total_qty) || 0) % unitPerPackage} ชิ้น
                </Text>
              )}
          </div>
        );
      },
    },
    {
      title: "คงเหลือ",
      dataIndex: "stock_balance",
      key: "stock_balance",
      align: "center",
      width: "10%",
      render: (text) => numeral(text).format("0,0.[00]"),
    },
    {
      title: <FaCogs />,
      key: "action",
      align: "center",
      width: "10%",
      render: (_text, { product_code }) => (
        <Popconfirm
          title="ต้องการลบรายการนี้หรือไม่"
          onConfirm={() => onDeleteProduct(product_code)}
          okText="ลบ"
          cancelText="ไม่ลบ"
          placement="topRight"
          zIndex={1080}
          disabled={!isEdit}
        >
          <Button size="small" ghost type="danger" disabled={!isEdit}>
            <FaTrashAlt size={10} />
          </Button>
        </Popconfirm>
      ),
    },
  ];
  return (
    <Table
      loading={!dataSource}
      dataSource={dataSource}
      columns={columns}
      rowKey="product_code"
      bordered
      footer={() => (
        <div style={{ display: "grid" }}>
          <Text strong>
            จำนวนรวม :{" "}
            {numeral(_.sumBy(dataSource ?? [], "total_qty")).format("0,0.[00]")}
          </Text>
          <Text strong>
            ปริมาตรรวม :{" "}
            {numeral(_.sumBy(dataSource ?? [], "total_volume")).format(
              "0,0.00",
            )}
          </Text>
        </div>
      )}
    />
  );
};

export default ManageProductTable;
